import TabSwitcher from "./TabSwitcher";

export default class Requirements {
  constructor() {
    this.container = this.findContainer();
    this.panels = [];
    this.tabSwitcher = null;
    this.hashChangeHandler = null;
    this.popStateHandler = null;
  }

  start() {
    this.container = this.findContainer();
    if (!this.container) return;

    this.panels = Array.from(
      this.container.querySelectorAll('.js-requirements-panel, .requirements-panel')
    );
    if (!this.panels.length) return;

    this.tabSwitcher = new TabSwitcher({
      findContainer: () => this.container,
      buttonSelector: '.js-requirements-tab, .requirements-tab-nav__button',
      validateTabKey: (tabKey) =>
        this.panels.some((panel) => this.getPanelTabKey(panel) === tabKey),
      onTabActivated: ({ tabKey }) => {
        this.activatePanels(tabKey);
      },
    });
    this.tabSwitcher.start();
    this.bindLocationChange();
    const hasLocationTarget = this.hasRequestedLocationTarget();
    this.syncFromLocation({
      shouldScroll: hasLocationTarget,
      deferScroll: hasLocationTarget,
    });
  }

  reset() {
    this.destruct();
    this.start();
  }

  destruct() {
    if (this.hashChangeHandler) {
      window.removeEventListener('hashchange', this.hashChangeHandler);
      this.hashChangeHandler = null;
    }
    if (this.popStateHandler) {
      window.removeEventListener('popstate', this.popStateHandler);
      this.popStateHandler = null;
    }

    if (this.tabSwitcher) {
      this.tabSwitcher.destruct();
      this.tabSwitcher = null;
    }
  }

  findContainer() {
    return (
      document.querySelector('.js-requirements') ||
      document.querySelector('.requirements-layout') ||
      document.querySelector('main.requirements') ||
      null
    );
  }

  getPanelTabKey(panel) {
    if (!panel) return '';
    return (panel.dataset.tabPanel || panel.getAttribute('data-tab-panel') || '').trim();
  }

  getHashTabKey() {
    if (!window.location.hash) return '';
    try {
      return decodeURIComponent(window.location.hash.replace('#', '')).trim();
    } catch (error) {
      return window.location.hash.replace('#', '').trim();
    }
  }

  getSearchParams() {
    try {
      return new URLSearchParams(window.location.search || '');
    } catch (error) {
      return null;
    }
  }

  getRequestedTabKey() {
    const searchParams = this.getSearchParams();
    const tabParam = searchParams ? (searchParams.get('tab') || '').trim() : '';
    if (tabParam) return tabParam;
    return this.getHashTabKey();
  }

  getRequestedRequirementId() {
    const searchParams = this.getSearchParams();
    if (!searchParams) return 0;

    const rawId =
      (searchParams.get('requirement') || searchParams.get('requirement_id') || '').trim();
    if (!rawId) return 0;

    const parsedId = parseInt(rawId, 10);
    return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : 0;
  }

  hasRequestedLocationTarget() {
    const searchParams = this.getSearchParams();
    const hasSearchTarget = searchParams
      ? Boolean(
          searchParams.get('tab') ||
            searchParams.get('requirement') ||
            searchParams.get('requirement_id')
        )
      : false;

    return Boolean(this.getHashTabKey() || hasSearchTarget);
  }

  getPanelByTabKey(tabKey) {
    return this.panels.find((panel) => this.getPanelTabKey(panel) === tabKey) || null;
  }

  getCardByRequirementId(requirementId) {
    if (!requirementId) return null;
    const selector = `[data-requirement-id="${requirementId}"], #requirements-card-${requirementId}`;
    return this.container ? this.container.querySelector(selector) : null;
  }

  getPanelByCard(card) {
    if (!card) return null;
    return card.closest('.js-requirements-panel, .requirements-panel');
  }

  scrollToTarget(target) {
    if (!target) return;
    target.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  syncFromLocation({ shouldScroll = false, deferScroll = false } = {}) {
    if (!this.tabSwitcher) return;

    const requestedRequirementId = this.getRequestedRequirementId();
    const requestedCard = this.getCardByRequirementId(requestedRequirementId);
    const requestedCardPanel = this.getPanelByCard(requestedCard);
    const requestedCardTabKey = this.getPanelTabKey(requestedCardPanel);
    const requestedTabKey = this.getRequestedTabKey();
    const validRequestedTabKey = requestedTabKey
      ? this.tabSwitcher.findAvailableTab(requestedTabKey)
      : '';
    const tabKeyToActivate = validRequestedTabKey || requestedCardTabKey;

    if (tabKeyToActivate) {
      this.tabSwitcher.activate(tabKeyToActivate, false);
    }

    if (requestedCardTabKey && this.tabSwitcher.currentTab !== requestedCardTabKey) {
      this.tabSwitcher.activate(requestedCardTabKey, false);
    }

    const activeTabKey = this.tabSwitcher.currentTab || '';
    if (!shouldScroll) return;

    let scrollTarget = null;
    if (requestedRequirementId) {
      scrollTarget = this.getCardByRequirementId(requestedRequirementId);
    }

    if (!scrollTarget && activeTabKey) {
      scrollTarget = this.getPanelByTabKey(activeTabKey);
    }

    if (!scrollTarget) return;

    if (deferScroll) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.scrollToTarget(scrollTarget);
        });
      });
      return;
    }

    this.scrollToTarget(scrollTarget);
  }

  bindLocationChange() {
    this.hashChangeHandler = () => {
      this.syncFromLocation({ shouldScroll: true });
    };
    this.popStateHandler = () => {
      this.syncFromLocation({ shouldScroll: true });
    };
    window.addEventListener('hashchange', this.hashChangeHandler);
    window.addEventListener('popstate', this.popStateHandler);
  }

  activatePanels(tabKey) {
    this.panels.forEach((panel) => {
      const isActive = this.getPanelTabKey(panel) === tabKey;
      panel.classList.toggle('is-active', isActive);

      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', 'hidden');
      }
    });
  }
}
